import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Paper, Typography, Button, TextField, Grid, Table, TableHead, TableRow, TableCell, TableBody,
    MenuItem, FormControl, InputLabel, Select, FormControlLabel, Checkbox, Chip, Box, Dialog,
    DialogTitle, DialogContent, DialogContentText, DialogActions, TableContainer, Card,
    CardContent, useTheme, useMediaQuery, CircularProgress, Snackbar, Alert
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import IconButton from "@mui/material/IconButton";

import { getCoupons, createCoupon, updateCoupon, toggleCouponStatus, deleteCoupon } from "../api/adminApi";

// 👇 Imported your ScrollToTop utility
import ScrollToTop from "../utils/ScrollToTop";

const INITIAL_FORM_STATE = {
    code: "", coupon_name: "", discount_type: "PERCENTAGE", discount_value: "",
    max_discount: "", minimum_order_amount: "", usage_limit: "", is_first_order: false,
    one_time_per_user: false, is_special: false, valid_from: "", valid_to: ""
};

const modernInputStyle = {
    '& .MuiInputLabel-root': { color: '#A59D96', fontWeight: '600' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#D97706' },
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px', bgcolor: '#FFFFFF',
        '& fieldset': { borderColor: '#ECE5DA' },
        '&:hover fieldset': { borderColor: '#F29D17' },
        '&.Mui-focused fieldset': { borderColor: '#F8C332', borderWidth: '2px' }
    },
    '& .MuiOutlinedInput-input': { fontWeight: '700', color: '#2F2A25' }
};

export default function Coupons({ zonalAdminId }) {
    const navigate = useNavigate();

    const { id: urlZonalAdminId } = useParams();
    const activeAdminId = zonalAdminId || urlZonalAdminId;

    const [coupons, setCoupons] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingCoupon, setEditingCoupon] = useState(null);

    const [isLoading, setIsLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const [confirmDialog, setConfirmDialog] = useState({ open: false, type: '', coupon: null });
    const [form, setForm] = useState(INITIAL_FORM_STATE);

    const theme = useTheme();
    const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const showSnackbar = useCallback((message, severity = "success") => {
        setSnackbar({ open: true, message, severity });
    }, []);

    const loadCoupons = useCallback(async () => {
        setIsLoading(true);
        try {
            const params = activeAdminId ? { zonal_admin_id: activeAdminId } : {};
            const res = await getCoupons(params);
            setCoupons(res.data || []);
        } catch (err) {
            console.error(err);
            showSnackbar("Failed to load coupons", "error");
        } finally {
            setIsLoading(false);
        }
    }, [showSnackbar, activeAdminId]);

    useEffect(() => { loadCoupons(); }, [loadCoupons]);

    const handleEdit = (coupon) => {
        setEditingCoupon(coupon);
        setForm({
            code: coupon.code || "", coupon_name: coupon.coupon_name || "",
            discount_type: coupon.discount_type || "PERCENTAGE", discount_value: coupon.discount_value || "",
            max_discount: coupon.max_discount || "", minimum_order_amount: coupon.minimum_order_amount || "",
            usage_limit: coupon.usage_limit || "", is_first_order: coupon.is_first_order || false,
            one_time_per_user: coupon.one_time_per_user || false, is_special: coupon.is_special || false,
            valid_from: coupon.valid_from ? coupon.valid_from.slice(0, 16) : "",
            valid_to: coupon.valid_to ? coupon.valid_to.slice(0, 16) : "",
        });
        setOpenDialog(true);
    };

    const openConfirm = (type, coupon) => { setConfirmDialog({ open: true, type, coupon }); };
    const closeConfirm = () => { setConfirmDialog({ open: false, type: '', coupon: null }); };

    const handleConfirmAction = async () => {
        const { type, coupon } = confirmDialog;
        if (!coupon) return;
        try {
            if (type === 'TOGGLE') {
                await toggleCouponStatus(coupon.id);
                showSnackbar(`Coupon ${coupon.active ? 'deactivated' : 'activated'} successfully`, "success");
            } else if (type === 'DELETE') {
                await deleteCoupon(coupon.id);
                showSnackbar("Coupon deleted successfully", "success");
            }
            loadCoupons();
        } catch (err) {
            console.error(err);
            showSnackbar(`Failed to ${type === 'TOGGLE' ? 'update status' : 'delete coupon'}`, "error");
        } finally {
            closeConfirm();
        }
    };

    const handleCreate = async () => {
        if (!form.code.trim()) return showSnackbar("Coupon Code is required", "warning");
        if (!form.discount_value) return showSnackbar("Discount Value is required", "warning");
        if (!form.valid_from || !form.valid_to) return showSnackbar("Valid From and Valid To dates are required", "warning");

        const payload = {
            ...form,
            discount_value: parseFloat(form.discount_value),
            max_discount: form.max_discount === "" ? null : parseFloat(form.max_discount),
            minimum_order_amount: form.minimum_order_amount === "" ? 0 : parseFloat(form.minimum_order_amount),
            usage_limit: form.usage_limit === "" ? null : parseInt(form.usage_limit, 10),
        };

        try {
            if (editingCoupon) {
                await updateCoupon(editingCoupon.id, payload);
                showSnackbar("Coupon updated successfully", "success");
            } else {
                const params = activeAdminId ? { zonal_admin_id: activeAdminId } : {};
                await createCoupon(payload, params);
                showSnackbar("Coupon created successfully", "success");
            }
            setEditingCoupon(null);
            setForm(INITIAL_FORM_STATE);
            setOpenDialog(false);
            loadCoupons();
        } catch (err) {
            console.error(err);
            showSnackbar(editingCoupon ? "Failed to update coupon" : "Failed to create coupon", "error");
        }
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: 1200, mx: "auto", minHeight: "100vh", bgcolor: "#FFFDF8", mb: 10 }}>
            {/* 👇 Added ScrollToTop component here */}
            <ScrollToTop />

            <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={2} mb={4}>
                <Box display="flex" alignItems="center" gap={1.5}>
                    <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: '#FFFFFF', border: '1px solid #ECE5DA', color: '#3A2C23', '&:hover': { bgcolor: '#F8C332', borderColor: '#F8C332' } }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h5" fontWeight="900" sx={{ color: "#3A2C23", letterSpacing: "-0.5px" }}>
                        Coupon Management
                    </Typography>
                </Box>

                <Button
                    variant="contained" size="large" disableElevation
                    onClick={() => {
                        setEditingCoupon(null);
                        setForm(INITIAL_FORM_STATE);
                        setOpenDialog(true);
                    }}
                    sx={{
                        borderRadius: '16px', textTransform: "none", fontWeight: 900, bgcolor: '#F8C332',
                        color: '#3A2C23', boxShadow: '0 4px 12px rgba(248, 195, 50, 0.3)',
                        '&:hover': { bgcolor: '#F5B71D' }, py: { xs: 1.5, sm: 1.2 }, px: 3, width: { xs: "100%", sm: "auto" }
                    }}
                >
                    + Create Coupon
                </Button>
            </Box>

            {isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress size={60} thickness={4} sx={{ color: '#F8C332' }} />
                </Box>
            ) : isMobile ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {coupons.map((c) => (
                        <Card key={c.id} elevation={0} sx={{ borderRadius: '20px', border: "none", bgcolor: '#FFFFFF', boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)', transition: 'transform 0.2s ease, box-shadow 0.2s ease', '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 16px 32px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)' } }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="h6" fontWeight="900" sx={{ color: '#E85D3F' }}>{c.code}</Typography>
                                        {c.is_special && (<Chip label="Hidden" size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#FFF0EE', color: '#E85D3F', fontWeight: 800 }} />)}
                                    </Box>
                                    <Chip label={c.active ? "Active" : "Inactive"} color={c.active ? "success" : "default"} size="small" variant={c.active ? "filled" : "outlined"} sx={{ fontWeight: 800, borderRadius: '8px' }} />
                                </Box>

                                <Typography variant="subtitle1" fontWeight="800" sx={{ color: '#3A2C23' }} gutterBottom>{c.coupon_name}</Typography>

                                <Grid container spacing={1.5} sx={{ mt: 1 }}>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" fontWeight="700" display="block">Discount</Typography>
                                        <Typography variant="body2" fontWeight="800" color="#2F2A25">{c.discount_type === "PERCENTAGE" ? `${c.discount_value}%` : `₹${c.discount_value}`}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" fontWeight="700" display="block">Min Order</Typography>
                                        <Typography variant="body2" fontWeight="800" color="#2F2A25">₹{c.minimum_order_amount}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" fontWeight="700" display="block">Usage</Typography>
                                        <Typography variant="body2" fontWeight="800" color="#2F2A25">{c.used_count} / {c.usage_limit || "∞"}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" fontWeight="700" display="block">First Order Only</Typography>
                                        <Typography variant="body2" fontWeight="800" color="#2F2A25">{c.is_first_order ? "Yes" : "No"}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" fontWeight="700" display="block">One Time Use</Typography>
                                        <Typography variant="body2" fontWeight="800" color="#2F2A25">{c.one_time_per_user ? "Yes" : "No"}</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary" fontWeight="700" display="block">Valid Until</Typography>
                                        <Typography variant="body2" fontWeight="800" color="#2F2A25">{c.valid_to ? new Date(c.valid_to).toLocaleDateString() : "No Expiry"}</Typography>
                                    </Grid>
                                </Grid>
                                <Box mt={2.5} pt={2} borderTop="1px dashed #ECE5DA" display="flex" justifyContent="flex-end" gap={1}>
                                    <IconButton sx={{ color: '#F29D17', bgcolor: '#FFF6DE', '&:hover': { bgcolor: '#F8C332', color: '#3A2C23' } }} onClick={() => handleEdit(c)}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton sx={{ color: '#E85D3F', bgcolor: '#FFF0EE', '&:hover': { bgcolor: '#E85D3F', color: '#FFF' } }} onClick={() => openConfirm('DELETE', c)}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                    <Button size="small" variant="contained" disableElevation color={c.active ? "inherit" : "success"} onClick={() => openConfirm('TOGGLE', c)} sx={{ fontWeight: 800, borderRadius: '8px', textTransform: 'none', bgcolor: c.active ? '#F2EEE8' : '#DDECC8', color: c.active ? '#6E655E' : '#577A34', '&:hover': { bgcolor: c.active ? '#E5E0D8' : '#CCE3AF' } }}>
                                        {c.active ? "Deactivate" : "Activate"}
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}

                    {coupons.length === 0 && (
                        <Paper elevation={0} sx={{ p: 4, textAlign: "center", borderRadius: '20px', border: "1px dashed #ECE5DA", bgcolor: '#FFFFFF' }}>
                            <Typography color="text.secondary" fontWeight="700">No coupons found for this zone. Create one to get started!</Typography>
                        </Paper>
                    )}
                </Box>
            ) : (
                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '20px', border: "none", bgcolor: '#FFFFFF', boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)', transition: 'transform 0.2s ease, box-shadow 0.2s ease', '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 16px 32px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)' } }}>
                    <Table sx={{ minWidth: 800 }}>
                        <TableHead sx={{ bgcolor: "#FFFDF8" }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Code</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Value</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Min Order</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Usage</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>First Order</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>One Time Use</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Expiry</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#A59D96' }} align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {coupons.map((c) => (
                                <TableRow key={c.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography variant="body2" fontWeight="900" sx={{ color: '#E85D3F' }}>{c.code}</Typography>
                                            {c.is_special && (<Chip label="Hidden" size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#FFF0EE', color: '#E85D3F', fontWeight: 800 }} />)}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#3A2C23' }}>{c.coupon_name}</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#6E655E' }}>{c.discount_type}</TableCell>
                                    <TableCell sx={{ fontWeight: 800, color: '#2F2A25' }}>{c.discount_type === "PERCENTAGE" ? `${c.discount_value}%` : `₹${c.discount_value}`}</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#6E655E' }}>₹{c.minimum_order_amount}</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#6E655E' }}>{c.used_count} / {c.usage_limit || "∞"}</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#6E655E' }}>{c.is_first_order ? "Yes" : "No"}</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#6E655E' }}>{c.one_time_per_user ? "Yes" : "No"}</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#6E655E' }}>{c.valid_to ? new Date(c.valid_to).toLocaleDateString() : "-"}</TableCell>
                                    <TableCell>
                                        <Chip label={c.active ? "Active" : "Inactive"} color={c.active ? "success" : "default"} size="small" variant={c.active ? "filled" : "outlined"} sx={{ fontWeight: 800, borderRadius: '6px' }} />
                                    </TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        <IconButton size="small" sx={{ color: '#F29D17', bgcolor: '#FFF6DE', '&:hover': { bgcolor: '#F8C332', color: '#3A2C23' }, mr: 1 }} onClick={() => handleEdit(c)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" sx={{ color: '#E85D3F', bgcolor: '#FFF0EE', '&:hover': { bgcolor: '#E85D3F', color: '#FFF' }, mr: 1 }} onClick={() => openConfirm('DELETE', c)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                        <Button size="small" variant="contained" disableElevation color={c.active ? "inherit" : "success"} onClick={() => openConfirm('TOGGLE', c)} sx={{ textTransform: "none", fontWeight: 800, borderRadius: '8px', bgcolor: c.active ? '#F2EEE8' : '#DDECC8', color: c.active ? '#6E655E' : '#577A34', '&:hover': { bgcolor: c.active ? '#E5E0D8' : '#CCE3AF' } }}>
                                            {c.active ? "Deactivate" : "Activate"}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}

                            {coupons.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={11} align="center" sx={{ py: 6, color: 'text.secondary', fontWeight: 700 }}>
                                        No coupons found for this zone. Create one to get started!
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullScreen={fullScreenDialog} maxWidth="md" fullWidth scroll="body" PaperProps={{ sx: { borderRadius: fullScreenDialog ? 0 : '24px', bgcolor: '#FFFDF8', boxShadow: '0 24px 48px rgba(0,0,0,0.2)' } }}>
                <DialogTitle sx={{ fontWeight: 900, pb: 2, pt: 3, px: { xs: 2, sm: 4 }, color: '#3A2C23', borderBottom: '1px dashed #ECE5DA' }}>{editingCoupon ? "Edit Coupon" : "Create New Coupon"}</DialogTitle>
                <DialogContent sx={{ p: { xs: 2, sm: 4 }, pt: "24px !important", pb: { xs: '15vh', sm: 4 } }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6} md={4}><TextField sx={modernInputStyle} fullWidth label="Coupon Code" variant="outlined" value={form.code} required onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} /></Grid>
                        <Grid item xs={12} sm={6} md={4}><TextField sx={modernInputStyle} fullWidth label="Coupon Name" value={form.coupon_name} onChange={(e) => setForm({ ...form, coupon_name: e.target.value })} /></Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl fullWidth sx={modernInputStyle}>
                                <InputLabel>Discount Type</InputLabel>
                                <Select value={form.discount_type} label="Discount Type" onChange={(e) => setForm({ ...form, discount_type: e.target.value, max_discount: e.target.value === "FLAT" ? "" : form.max_discount })}>
                                    <MenuItem value="PERCENTAGE" sx={{ fontWeight: 600 }}>Percentage</MenuItem>
                                    <MenuItem value="FLAT" sx={{ fontWeight: 600 }}>Flat Amount</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}><TextField sx={modernInputStyle} type="number" fullWidth label="Discount Value" value={form.discount_value} required onChange={(e) => setForm({ ...form, discount_value: e.target.value })} /></Grid>
                        {form.discount_type === "PERCENTAGE" && (<Grid item xs={12} sm={6} md={4}><TextField sx={modernInputStyle} type="number" fullWidth label="Maximum Discount" value={form.max_discount} onChange={(e) => setForm({ ...form, max_discount: e.target.value })} /></Grid>)}
                        <Grid item xs={12} sm={6} md={4}><TextField sx={modernInputStyle} type="number" fullWidth label="Minimum Order Amount" value={form.minimum_order_amount} onChange={(e) => setForm({ ...form, minimum_order_amount: e.target.value })} /></Grid>
                        <Grid item xs={12} sm={6} md={4}><TextField sx={modernInputStyle} type="number" fullWidth label="Usage Limit" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} /></Grid>
                        <Grid item xs={12} sm={6} md={4}><TextField sx={modernInputStyle} fullWidth type="datetime-local" label="Valid From" InputLabelProps={{ shrink: true }} value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} /></Grid>
                        <Grid item xs={12} sm={6} md={4}><TextField sx={modernInputStyle} fullWidth type="datetime-local" label="Valid To" InputLabelProps={{ shrink: true }} value={form.valid_to} onChange={(e) => setForm({ ...form, valid_to: e.target.value })} /></Grid>
                        <Grid item xs={12}>
                            <Box sx={{ bgcolor: "#FFFFFF", p: 2, borderRadius: '16px', border: "1px solid #ECE5DA", boxShadow: '0 4px 12px rgba(0,0,0,0.03)', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <FormControlLabel control={<Checkbox checked={form.is_first_order} sx={{ color: '#F29D17', '&.Mui-checked': { color: '#F8C332' } }} onChange={(e) => setForm({ ...form, is_first_order: e.target.checked })} />} label={<Typography fontWeight="700" color="#3A2C23">Restrict to First Order Only</Typography>} />
                                <FormControlLabel control={<Checkbox checked={form.one_time_per_user} sx={{ color: '#F29D17', '&.Mui-checked': { color: '#F8C332' } }} onChange={(e) => setForm({ ...form, one_time_per_user: e.target.checked })} />} label={<Typography fontWeight="700" color="#3A2C23">One Time Use Per User</Typography>} />
                                <FormControlLabel control={<Checkbox checked={form.is_special} sx={{ color: '#F29D17', '&.Mui-checked': { color: '#F8C332' } }} onChange={(e) => setForm({ ...form, is_special: e.target.checked })} />} label={<Typography fontWeight="700" color="#3A2C23">Special Coupon (Hidden, requires manual code)</Typography>} />
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: { xs: 2, sm: 3 }, px: { sm: 4 }, borderTop: "1px solid #ECE5DA", bgcolor: '#FFFFFF', borderRadius: '0 0 24px 24px' }}>
                    <Button onClick={() => setOpenDialog(false)} color="inherit" sx={{ textTransform: "none", fontWeight: 800, color: '#6E655E', borderRadius: '12px', px: 3 }}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreate} disableElevation sx={{ textTransform: "none", fontWeight: 900, borderRadius: '12px', bgcolor: '#F8C332', color: '#3A2C23', px: 4, py: 1.2, boxShadow: '0 4px 12px rgba(248, 195, 50, 0.3)', '&:hover': { bgcolor: '#F5B71D' } }}>{editingCoupon ? "Update Coupon" : "Save Coupon"}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmDialog.open} onClose={closeConfirm} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '20px', bgcolor: '#FFFDF8' } }}>
                <DialogTitle sx={{ fontWeight: 900, color: '#3A2C23' }}>Confirm Action</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: "#6E655E", fontWeight: 600 }}>Are you sure you want to {confirmDialog.type === 'TOGGLE' ? (confirmDialog.coupon?.active ? 'deactivate' : 'activate') : 'delete'} the coupon <Typography component="span" fontWeight="900" color="#E85D3F">{confirmDialog.coupon?.code}</Typography>?</DialogContentText>
                    {confirmDialog.type === 'DELETE' && (<DialogContentText sx={{ color: "#DC2626", mt: 2, fontSize: "0.875rem", fontWeight: 700 }}>This action is permanent and cannot be undone.</DialogContentText>)}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeConfirm} sx={{ fontWeight: 800, color: '#6E655E', textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={handleConfirmAction} variant="contained" disableElevation sx={{ fontWeight: 800, borderRadius: '10px', textTransform: 'none', bgcolor: confirmDialog.type === 'DELETE' ? '#DC2626' : '#3A2C23', color: '#FFFFFF', '&:hover': { bgcolor: confirmDialog.type === 'DELETE' ? '#B91C1C' : '#2F2A25' } }}>Confirm</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%', fontWeight: 700, borderRadius: '12px' }} variant="filled">{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
}